import React, { useState, useEffect, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import config from '../config';
import { useAuth } from '../context/AuthContext';
import { useApplicationContext } from '../context/ApplicationContext';
import ControlTowerMap from './child/ControlTowerMap';

const VenueManagementLayer = () => {
    const { auth } = useAuth();
    const navigate = useNavigate();
    const { setActiveTabId } = useApplicationContext();
    const [venues, setVenues] = useState([]);
    const [loading, setLoading] = useState(true);
    const [limits, setLimits] = useState(null);
    const [deleteModal, setDeleteModal] = useState({ show: false, venue: null });
    const [viewMode, setViewMode] = useState('table'); // 'table' | 'map'

    // Fetch venues and limits
    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [venuesRes, limitsRes] = await Promise.all([
                auth.fetch(`${config.API_BASE_URL}/api/v2/venues/my-venues`),
                auth.fetch(`${config.API_BASE_URL}/api/v2/venues/limits`)
            ]);

            if (venuesRes.ok) {
                const venuesData = await venuesRes.json();
                // API returns { venues, subscription, usage } - extract venues array
                setVenues(Array.isArray(venuesData) ? venuesData : (venuesData.venues || []));
            }

            if (limitsRes.ok) {
                const limitsData = await limitsRes.json();
                setLimits(limitsData);
            }
        } catch (error) {
            console.error('Error fetching venues:', error);
            toast.error('Failed to load venues');
        } finally {
            setLoading(false);
        }
    }, [auth]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleDelete = async () => {
        if (!deleteModal.venue) return;

        try {
            const response = await auth.fetch(
                `${config.API_BASE_URL}/api/v2/venues/${deleteModal.venue.id}`,
                { method: 'DELETE' }
            );

            if (response.ok) {
                toast.success('Venue deleted successfully');
                setDeleteModal({ show: false, venue: null });
                fetchData();
            } else {
                const error = await response.json();
                toast.error(error.message || 'Failed to delete venue');
            }
        } catch (error) {
            console.error('Error deleting venue:', error);
            toast.error('Failed to delete venue');
        }
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            pending: { cls: 'hs-badge-warning', icon: 'solar:clock-circle-bold', label: 'Pending' },
            approved: { cls: 'hs-badge-success', icon: 'solar:check-circle-bold', label: 'Approved' },
            rejected: { cls: 'hs-badge-danger', icon: 'solar:close-circle-bold', label: 'Rejected' },
            suspended: { cls: 'hs-badge-neutral', icon: 'solar:danger-triangle-bold', label: 'Suspended' },
            banned: { cls: 'hs-badge-danger', icon: 'solar:shield-cross-bold', label: 'Banned' }
        };
        const s = statusMap[status] || statusMap.pending;
        return (
            <span className={`hs-badge ${s.cls}`}>
                <Icon icon={s.icon} className="text-sm" />
                {s.label}
            </span>
        );
    };

    const canAddVenue = limits?.limits?.venues 
        ? limits.limits.venues.current < limits.limits.venues.max || limits.limits.venues.max === -1
        : true;

    const getLimitProgress = () => {
        if (!limits?.limits?.venues) return null;
        const { current, max } = limits.limits.venues;
        if (max === -1) return null; // Unlimited
        const percentage = Math.min((current / max) * 100, 100);
        const color = percentage >= 90 ? 'bg-danger' : percentage >= 70 ? 'bg-warning' : 'bg-primary';
        return { current, max, percentage, color };
    };

    const limitProgress = getLimitProgress();
    
    const handleUpgrade = (e) => {
        if (e) e.preventDefault();
        setActiveTabId('my-plan');
        navigate('/applications');
    };

    if (loading) {
        return (
            <div className="hs-card p-0">
                <div className="hs-card-body d-flex justify-content-center align-items-center" style={{ minHeight: '360px' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading venues...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            {/* Limit Progress Banner */}
            {limitProgress && (
                <div className="hs-card mb-24" style={{ background: 'linear-gradient(135deg, rgba(2,132,199,0.03) 0%, rgba(2,132,199,0.08) 100%)' }}>
                    <div className="hs-card-body p-20">
                        <div className="d-flex justify-content-between align-items-center mb-10 flex-wrap gap-2">
                            <div className="d-flex align-items-center gap-12">
                                <div className="w-48-px h-48-px rounded-12 bg-primary-100 text-primary-600 d-flex align-items-center justify-content-center flex-shrink-0">
                                    <Icon icon="solar:shop-2-bold" className="text-2xl" />
                                </div>
                                <div>
                                    <h6 className="fw-bold mb-0 text-dark">Venue Capacity Usage</h6>
                                    <small className="text-secondary-light">Based on your active subscription plan</small>
                                </div>
                            </div>
                            <div className="d-flex align-items-center gap-3">
                                <span className="hs-badge hs-badge-info fw-bold">
                                    {limitProgress.current} / {limitProgress.max} Venues Used
                                </span>
                                {limitProgress.percentage >= 70 && (
                                    <button 
                                        type="button" 
                                        onClick={handleUpgrade} 
                                        className="hs-btn-primary hs-btn-sm"
                                    >
                                        <Icon icon="solar:crown-star-bold" />
                                        Upgrade Plan
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="progress mt-12" style={{ height: '8px', borderRadius: '6px', backgroundColor: 'rgba(226, 232, 240, 0.8)' }}>
                            <div 
                                className="progress-bar hs-progress-gradient" 
                                role="progressbar" 
                                style={{ width: `${limitProgress.percentage}%`, borderRadius: '6px', transition: 'width 0.4s ease' }}
                                aria-valuenow={limitProgress.percentage} 
                                aria-valuemin="0" 
                                aria-valuemax="100"
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Venues Table Container */}
            <div className="hs-card">
                <div className="hs-card-header flex-wrap gap-3">
                    <div className="d-flex align-items-center gap-3">
                        <h6 className="fw-bold mb-0 text-dark">My Registered Venues</h6>
                        <span className="hs-badge hs-badge-info">
                            {venues.length} {venues.length === 1 ? 'Venue' : 'Venues'}
                        </span>
                    </div>
                    <div className="d-flex align-items-center gap-12 flex-wrap">
                        {venues.length > 0 && (
                            <div className="btn-group border radius-8 overflow-hidden bg-white shadow-xs" role="group">
                                <button
                                    type="button"
                                    className={`btn btn-sm d-inline-flex align-items-center gap-1 px-12 py-6 border-0 ${viewMode === 'table' ? 'bg-primary-600 text-white fw-semibold' : 'bg-white text-secondary-light'}`}
                                    onClick={() => setViewMode('table')}
                                >
                                    <Icon icon="solar:list-bold" />
                                    <span>Table View</span>
                                </button>
                                <button
                                    type="button"
                                    className={`btn btn-sm d-inline-flex align-items-center gap-1 px-12 py-6 border-0 ${viewMode === 'map' ? 'bg-primary-600 text-white fw-semibold' : 'bg-white text-secondary-light'}`}
                                    onClick={() => setViewMode('map')}
                                >
                                    <Icon icon="solar:map-bold" />
                                    <span>Map Preview</span>
                                </button>
                            </div>
                        )}
                        <button
                            className="hs-btn-primary"
                            onClick={() => navigate('/add-venue')}
                            disabled={!canAddVenue}
                            title={!canAddVenue ? 'Venue limit reached. Upgrade your plan.' : ''}
                        >
                            <Icon icon="solar:add-circle-bold" className="text-lg" />
                            Add New Venue
                        </button>
                    </div>
                </div>

                <div className="hs-card-body p-0">
                    {venues.length === 0 ? (
                        <div className="hs-empty-state">
                            <div className="hs-empty-icon">
                                <Icon icon="solar:shop-2-bold" />
                            </div>
                            <h5 className="hs-empty-title">No Venues Registered Yet</h5>
                            <p className="hs-empty-desc">
                                Create your first business venue profile to publish menus, photos, announcements, and connect with customers.
                            </p>
                            <button
                                className="hs-btn-primary"
                                onClick={() => navigate('/add-venue')}
                                disabled={!canAddVenue}
                            >
                                <Icon icon="solar:add-circle-bold" className="text-lg" />
                                Create Your First Venue
                            </button>
                        </div>
                    ) : viewMode === 'map' ? (
                        <div className="p-20">
                            <ControlTowerMap
                                venues={venues}
                                height="540px"
                                title="My Venues Map Preview"
                            />
                        </div>
                    ) : (
                        <div className="hs-table-wrapper">
                            <table className="hs-table mb-0">
                                <thead>
                                    <tr>
                                        <th scope="col">Venue Profile</th>
                                        <th scope="col">Location</th>
                                        <th scope="col">Verification</th>
                                        <th scope="col">Content & Features</th>
                                        <th scope="col" className="text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {venues.map((venue) => (
                                        <tr key={venue.id}>
                                            <td>
                                                <div className="d-flex align-items-center gap-12">
                                                    {venue.logo_url ? (
                                                        <img 
                                                            src={venue.logo_url} 
                                                            alt={venue.title}
                                                            className="w-48-px h-48-px rounded-12 object-fit-cover shadow-sm border"
                                                        />
                                                    ) : (
                                                        <div className="w-48-px h-48-px rounded-12 bg-primary-50 text-primary-600 border d-flex align-items-center justify-content-center">
                                                            <Icon icon="solar:shop-bold" className="text-2xl" />
                                                        </div>
                                                    )}
                                                    <div>
                                                        <h6 className="text-md fw-bold mb-1">
                                                            <Link to={`/venue/${venue.id}`} className="text-dark hover-text-primary text-decoration-none">
                                                                {venue.title || venue.business_name}
                                                            </Link>
                                                        </h6>
                                                        <span className="text-xs text-secondary-light line-clamp-1">
                                                            {venue.description?.substring(0, 60) || 'No description provided'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    <Icon icon="solar:map-point-bold" className="text-secondary-light text-base" />
                                                    <div>
                                                        <div className="text-sm fw-medium text-dark">
                                                            {venue.address_city || venue.address?.split(',')[1]?.trim() || '-'}
                                                        </div>
                                                        <small className="text-secondary-light">
                                                            {venue.address_country || venue.address?.split(',').pop()?.trim() || ''}
                                                        </small>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="d-flex flex-column align-items-start gap-2">
                                                    {getStatusBadge(venue.verification_status || 'pending')}
                                                    {['rejected', 'suspended', 'banned'].includes(venue.verification_status) && (
                                                        <>
                                                            <small
                                                                className="text-danger-main"
                                                                title={venue.verification_notes || 'Contact support for details'}
                                                                style={{ maxWidth: '240px' }}
                                                            >
                                                                {venue.verification_notes || 'Contact support for details'}
                                                            </small>
                                                            {venue.verification_status === 'rejected' && (
                                                                <Link to={`/edit-venue/${venue.id}`} className="text-primary-600 text-xs fw-semibold">
                                                                    Fix and resubmit
                                                                </Link>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="d-flex gap-8 flex-wrap">
                                                    <span className="hs-chip" title="Likes">
                                                        <Icon icon="solar:heart-bold" className="text-danger-600" />
                                                        {venue.likes_count || 0}
                                                    </span>
                                                    <span className="hs-chip" title="Gallery Images">
                                                        <Icon icon="solar:gallery-bold" className="text-primary-600" />
                                                        {venue.gallery_count || 0}
                                                    </span>
                                                    <span className="hs-chip" title="Features & Amenities">
                                                        <Icon icon="solar:star-bold" className="text-warning-600" />
                                                        {venue.feature_count || 0}
                                                    </span>
                                                    <span className="hs-chip" title="Blog Posts & Feeds">
                                                        <Icon icon="solar:document-text-bold" className="text-info-600" />
                                                        {venue.blog_count || 0}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="text-center">
                                                <div className="d-flex align-items-center gap-8 justify-content-center">
                                                    <button
                                                        type="button"
                                                        className="hs-icon-btn"
                                                        onClick={() => navigate(`/venue/${venue.id}`)}
                                                        title="View Venue Hub"
                                                        aria-label="View Venue Hub"
                                                    >
                                                        <Icon icon="solar:eye-bold" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="hs-icon-btn"
                                                        onClick={() => navigate(`/edit-venue/${venue.id}`)}
                                                        title="Edit Venue"
                                                        aria-label="Edit Venue"
                                                    >
                                                        <Icon icon="solar:pen-bold" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="hs-icon-btn text-danger-main"
                                                        onClick={() => setDeleteModal({ show: true, venue })}
                                                        title="Delete Venue"
                                                        aria-label="Delete Venue"
                                                    >
                                                        <Icon icon="solar:trash-bin-trash-bold" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {deleteModal.show && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content hs-card p-0 border-0">
                            <div className="modal-header hs-card-header">
                                <h6 className="fw-bold mb-0 text-dark">Confirm Venue Deletion</h6>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={() => setDeleteModal({ show: false, venue: null })}
                                    aria-label="Close"
                                />
                            </div>
                            <div className="modal-body p-24 text-center">
                                <div className="w-56-px h-56-px rounded-circle bg-danger-50 text-danger-600 mx-auto d-flex align-items-center justify-content-center mb-16">
                                    <Icon icon="solar:danger-triangle-bold" className="text-3xl" />
                                </div>
                                <h6 className="fw-bold text-dark mb-8">
                                    Delete "{deleteModal.venue?.title || deleteModal.venue?.business_name}"?
                                </h6>
                                <p className="text-secondary-light text-sm mb-0">
                                    This permanent action will remove the venue profile, gallery media, menu categories, items, and all related posts. This cannot be undone.
                                </p>
                            </div>
                            <div className="modal-footer hs-card-header justify-content-end gap-2 bg-neutral-50">
                                <button 
                                    type="button" 
                                    className="hs-btn-secondary hs-btn-sm"
                                    onClick={() => setDeleteModal({ show: false, venue: null })}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className="hs-btn-primary hs-btn-sm bg-danger border-0"
                                    onClick={handleDelete}
                                >
                                    <Icon icon="solar:trash-bin-trash-bold" />
                                    Delete Permanently
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default VenueManagementLayer;
